import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';

import Type from '../Typography';
import { call, getRef, SmallContext } from '../utils';

import styles from './Button.less';

const cx = classnames.bind(styles);

/**
 * **Access:**
 * ```js
 * import { Button } from '@iss/client-react-components'
 * ```
 *
 */
export default class Button extends React.PureComponent {
  static Caution = React.forwardRef((props, ref) => <Button {...props} ref={ref} kind="caution" />);
  static CautionAlternate = React.forwardRef((props, ref) => <Button {...props} ref={ref} kind="caution-alternate" />);
  static Alternate = React.forwardRef((props, ref) => <Button {...props} ref={ref} kind="alternate" />);
  static Secondary = React.forwardRef((props, ref) => <Button {...props} ref={ref} kind="secondary" />);
  static SecondaryDark = React.forwardRef((props, ref) => <Button {...props} ref={ref} kind="secondary-dark" />);
  static Special = React.forwardRef((props, ref) => <Button {...props} ref={ref} kind="special" />);
  static Primary = React.forwardRef((props, ref) => <Button {...props} ref={ref} kind="primary" />);
  static Link = React.forwardRef((props, ref) => <Button {...props} ref={ref} kind="link" />);

  static contextType = SmallContext;

  static propTypes = {
    /** @ignore */
    className: PropTypes.string,

    /** Change the component used to render. */
    as: PropTypes.any.isRequired,

    /** Selects which style to use */
    kind: PropTypes.oneOf([
      'caution',
      'caution-alternate',
      'primary',
      'secondary',
      'secondary-dark',
      'alternate',
      'special',
      'link',
    ]).isRequired,

    /** Enable small/mini mode */
    small: PropTypes.bool,

    /**
     * Render an icon. If string, assumed to be a URL, otherwise its passed as a node.
     */
    icon: PropTypes.node,

    /** Action to perform on click */
    onClick: PropTypes.func,
  };

  static defaultProps = {
    as: 'button',
    kind: 'primary',
  };

  ref = React.createRef();

  getDOMNode() {
    return getRef(this);
  }

  onClick = (e) => {
    call(this.props.onClick, e);
  };

  render() {
    const { children, className, icon, small, kind, disabled, ...props } = this.props;
    const iconOnly = icon && React.Children.count(children) === 0;

    const cls = cx('button', className, kind, { icon, iconOnly, small: SmallContext.isSmall(this) });

    if (props.as === 'button' && kind !== 'primary' && props.type == null) {
      //All non-primary buttons should not submit/trigger validation by default (HTMLButton's default type is "submit")
      props.type = 'button';
    }

    return (
      <Type {...props} ref={this.ref} className={cls} onClick={this.onClick} disabled={disabled}>
        {typeof icon === 'string' ? <img alt="" src={icon} /> : icon}
        {children}
      </Type>
    );
  }
}
